import { makeAutoObservable } from 'mobx'
import { RootStore } from './rootStore'
import { ValidateType } from './validateType'
import { minBy } from 'lodash'
import { ErrorBag } from './errorBag'
import Translator from '../components/translator'
import Settings from "../components/settings";

export class ValidateCustomerController {

  /**
   * @returns {ValidateCustomerController}
   */
  static getInstance() {
    if (!this.instance) {
      this.instance = new ValidateCustomerController()
    }
    return this.instance
  }

  constructor() {
    makeAutoObservable(this)
  }

  static getFieldsInfo() {
    let cart = RootStore.getInstance().cart

    return {
      ean: {
        type: 'text',
        name: 'ean',
        title: Translator.translate('EAN-lokationsnr', 'customer'),
        customer_types: ['public'],
        required: ['public'],
        pattern: ValidateType.eanPattern(),
        onBlur(){
          return cart.validateContent()
        },
        callback: data => {
          return ValidateType.validateEan(data)
        }
      },
      location: {
        type: 'text',
        name: 'location',
        title: Translator.translate('Faktureringssted', 'customer'),
        customer_types: ['public', 'business'],
        required: ['public', 'business'],
        className: ['break-before']
      },
      department: {
        type: 'text',
        name: 'department',
        title: Translator.translate('Afdeling', 'customer'),
        customer_types: ['public'],
        required: [],
      },
      name: {
        type: 'text',
        name: 'name',
        title: Translator.translate('Fulde navn', 'customer'),
        customer_types: ['private'],
        required: ['private'],
        callback: data => {
          return ValidateType.validateWordCount(data, 2)
        }
      },
      invoice_email: {
        type: 'text',
        name: 'invoice_email',
        title: Translator.translate('E-mail til faktura', 'customer'),
        customer_types: ['business', 'private'],
        required: ['business', 'private'],
        pattern: ValidateType.emailPattern(),
        callback: data => {
          return ValidateType.validateEmail(data)
        }
      },
      address: {
        type: 'text',
        name: 'address',
        title: Translator.translate('Adresse', 'customer'),
        customer_types: ['public', 'business', 'private'],
        required: ['public', 'business', 'private'],
        className: ['break-before']
      },
      zip_code: {
        type: 'text',
        name: 'zip_code',
        title: Translator.translate('Postnr', 'customer'),
        customer_types: ['public', 'business', 'private'],
        required: ['public', 'business', 'private'],
        className: ['break-before'],
        pattern: ValidateType.zipCodePattern(),
        onBlur: (e) => {
          if(ValidateCustomerController.getInstance().validateField('city') === false && ValidateCustomerController.getInstance().validateField('zip_code') === true) {
            let city = Settings.getSetting('zip_codes','payment-address')?.[cart.customer.zip_code];

            if(city) {
              cart.customer.city = city;
              ValidateCustomerController.getInstance().validateField('city');
            }
          }
        },
        callback: data => {
          return ValidateType.validateZipCode(data)
        }
      },
      city: {
        type: 'text',
        name: 'city',
        title: Translator.translate('By', 'customer'),
        customer_types: ['public', 'business', 'private'],
        required: ['public', 'business', 'private'],
      },
      email: {
        type: 'text',
        name: 'email',
        title: Translator.translate('E-mail til samlet bekræftelse', 'customer'),
        customer_types: ['public', 'business', 'private'],
        required: ['public'],
        className: ['break-before'],
        pattern: ValidateType.emailPattern(),
        onBlur(){
          return cart.validateContent()
        },
        callback: data => {
          if (data == null || data == '') {
            return true
          }
          return ValidateType.validateEmail(data)
        }
      },
      phone: {
        type: 'text',
        name: 'phone',
        title: Translator.translate('Telefonnummer', 'customer'),
        customer_types: ['public', 'business', 'private'],
        required: ['public', 'business', 'private'],
        pattern: ValidateType.phonePattern(),
        callback: data => {
          return ValidateType.validatePhone(data)
        }
      },
      order_nr: {
        type: 'text',
        name: 'order_nr',
        title:Translator.translate('Ordre/rekvisitionsnr', 'customer'),
        customer_types: ['public', 'business'],
        required: [],
        className: ['break-before']
      },
      cvr: {
        type: 'text',
        name: 'cvr',
        title:Translator.translate('CVR', 'customer'),
        customer_types: ['business'],
        required: ['business'],
        pattern: ValidateType.cvrPattern(),
        onBlur(){
          return cart.validateContent()
        },
        callback: data => {
          return ValidateType.validateCvr(data)
        }
      },
      person_ref: {
        type: 'text',
        name: 'person_ref',
        title: Translator.translate('Personreference', 'customer'),
        customer_types: ['public', 'business', 'private'],
        required: [],
      }
    }
  }

  static getFieldsValdiation() {
    let fields = ValidateCustomerController.getFieldsInfo()

    fields.heard_about = {
      required: ['public', 'business', 'private'],
      customer_types: ['public', 'business', 'private']
    }

    return fields
  }

  static getRequiredForField(name) {
    let required = this.getFieldsValdiation()[name].required

    let cart = RootStore.getInstance().cart

    cart.eventGroups.forEach(eventGroup => {
      required = required.concat(eventGroup.requiredFields[name] ?? [])
    });

    return required.includes(cart.customer.type)
  }

  validateField(name) {
    const cart = RootStore.getInstance().cart

    const value = cart.customer[name] ?? null

    const customer_type = cart.customer.type ?? null

    let validation_info = ValidateCustomerController.getFieldsValdiation()[name]

    if (!validation_info) {
      ErrorBag.getInstance().set(name, {
        valid: true
      })
      return true
    }

    if (!validation_info.customer_types.includes(customer_type)) {
      ErrorBag.getInstance().set(name, {
        valid: true
      })
      return true
    }

    if (ValidateCustomerController.getRequiredForField(name) && ValidateType.validateRequired(value) !== true) {
      ErrorBag.getInstance().set(name, {
        valid: false,
        error: ValidateType.validateRequired(value)
      })
      return false
    }

    if (typeof validation_info.callback === 'function') {
      let validation = validation_info.callback(value)

      if (validation === true) {
        ErrorBag.getInstance().set(name, {
          valid: true
        })

        return true
      }

      ErrorBag.getInstance().set(name, {
        valid: false,
        error: validation
      })

      return false
    }

    ErrorBag.getInstance().set(name, {
      valid: true
    })
    return true
  }

  validateCustomerType() {
    const cart = RootStore.getInstance().cart

    const customer_type = cart.customer.type ?? null

    const allowed_customer_types = ['public', 'business', 'private']

    return allowed_customer_types.includes(customer_type)
  }

  resetValidation (key = null) {
    ErrorBag.getInstance().resetValidation(key)
  }



  validate() {
    this.resetValidation()
    if (this.validateCustomerType() !== true) {
      ErrorBag.getInstance().set('customer_type',{
        valid: false,
        error: Translator.translate('Du skal vælge en kundetype','type-validation')
      })
      this.scrollErrorsIntoView()
      return false
    }
    let returnValue = true
    for (const key of Object.keys(ValidateCustomerController.getFieldsValdiation())) {
      if (this.validateField(key) !== true) {
        returnValue = false
      }
    }

    this.scrollErrorsIntoView()
    return returnValue
  }

  isValid() {
    const errorbag = ErrorBag.getInstance()
    ErrorBag.instance = new ErrorBag();
    if (this.validateCustomerType() !== true) {
      ErrorBag.getInstance().set('customer_type',{
        valid: false,
        error: Translator.translate('Du skal vælge en kundetype','type-validation')
      })
      this.scrollErrorsIntoView()
      return false
    }
    let returnValue = true
    for (const key of Object.keys(ValidateCustomerController.getFieldsValdiation())) {
      if (this.validateField(key) !== true) {
        returnValue = false
      }
    }
    ErrorBag.instance = errorbag;
    return returnValue
  }

  scrollErrorsIntoView () {
    const element = minBy(ErrorBag.getInstance().getFieldsWithErrors(), r => r.getBoundingClientRect().top)
    if(element == null) {
      return
    }

    const headerOffset = 190
    const element_top = element.getBoundingClientRect().top
    if(element_top < headerOffset) {
      window.scrollTo({
        top: element_top + window.scrollY - headerOffset,
        behavior: 'smooth'
      })
    }

    const footerOffset = 24
    const element_bottom = element.getBoundingClientRect().bottom
    if(element_bottom > window.innerHeight - footerOffset) {
      window.scrollTo({
        top: window.scrollY + (element_bottom - window.innerHeight + footerOffset),
        behavior: 'smooth'
      })
    }

  }
}
